"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useId } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPalette, faTrophy } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import type { ArtistPreviewTab } from "@/hooks/useArtistPreviewModal";
const TOOLTIP_STYLES = {
  backgroundColor: "#1F2937",
  color: "white",
  padding: "6px 12px",
  fontSize: "12px",
  zIndex: 50,
  boxShadow: "0 4px 16px 0 rgba(0,0,0,0.30), 0 2px 8px 0 rgba(55,55,62,0.25)"
} as const;
const PALETTE_BUTTON_CLASS = "tw-relative tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-md tw-border tw-border-solid tw-border-blue-400/40 tw-bg-gradient-to-br tw-from-blue-800 tw-to-blue-900 tw-text-blue-100/90 tw-shadow-[0_1px_3px_rgba(0,0,0,0.45)] tw-transition-all tw-duration-200 tw-ease-out desktop-hover:hover:tw-from-blue-700 desktop-hover:hover:tw-to-blue-800 desktop-hover:hover:tw-scale-[1.05] focus:tw-ring-1 focus:tw-ring-blue-300/40";
const TROPHY_BUTTON_CLASS = "tw-relative tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-md tw-border tw-border-solid tw-border-amber-400/20 tw-bg-amber-400/10 tw-text-amber-300 tw-shadow-[inset_0_0_2px_rgba(255,255,255,0.08),0_1px_4px_rgba(0,0,0,0.25)] tw-transition-all tw-duration-200 desktop-hover:hover:tw-border-amber-400/30 desktop-hover:hover:tw-bg-amber-900/30 desktop-hover:hover:tw-scale-[1.05] desktop-hover:hover:tw-shadow-[inset_0_0_3px_rgba(255,255,255,0.12),0_2px_6px_rgba(0,0,0,0.3)] focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-amber-400/50 focus-visible:tw-ring-offset-2";
const PALETTE_ICON_CLASS = "tw-w-2.5 tw-h-2.5 tw-text-white tw-flex-shrink-0";
const TROPHY_ICON_CLASS = "tw-w-2.5 tw-h-2.5 tw-text-amber-400 tw-flex-shrink-0 desktop-hover:hover:tw-text-amber-300/90";
const BOTH_STATE_DOT_CLASS = "tw-pointer-events-none tw-absolute -tw-top-1 -tw-right-1 tw-size-[9px] tw-rounded-full tw-bg-blue-500 tw-border tw-border-solid tw-border-iron-900 tw-shadow-[0_0_0_1px_rgba(0,0,0,0.25)]";
type BadgeState = "none" | "active" | "winners" | "both";
function getBadgeState(submissionCount: number, trophyCount: number): BadgeState {
  const hasSubmissions = submissionCount > 0;
  const hasTrophyArtworks = trophyCount > 0;
  if (hasSubmissions && hasTrophyArtworks) return "both";
  if (hasSubmissions) return "active";
  if (hasTrophyArtworks) return "winners";
  return "none";
}
const BADGE_CONFIG: Record<Exclude<BadgeState, "none">, {
  readonly icon: typeof faPalette;
  readonly buttonClassName: string;
  readonly showDot: boolean;
  readonly initialTab: ArtistPreviewTab;
}> = {
  active: {
    icon: faPalette,
    buttonClassName: PALETTE_BUTTON_CLASS,
    showDot: false,
    initialTab: "active"
  },
  winners: {
    icon: faTrophy,
    buttonClassName: TROPHY_BUTTON_CLASS,
    showDot: false,
    initialTab: "winners"
  },
  both: {
    icon: faTrophy,
    buttonClassName: TROPHY_BUTTON_CLASS,
    showDot: true,
    initialTab: "active"
  }
};
function getTooltipContent(state: BadgeState, submissionCount: number, trophyCount: number): string {
  const submissionsLabel = `${submissionCount} art submission${submissionCount === 1 ? "" : "s"}`;
  const trophyLabel = `${trophyCount} minted meme${trophyCount === 1 ? "" : "s"}`;
  if (state === "both") {
    return `View ${submissionsLabel} and ${trophyLabel}`;
  }
  if (state === "active") {
    return `View ${submissionsLabel}`;
  }
  return `View ${trophyLabel}`;
}
interface ArtistActivityBadgeProps {
  readonly submissionCount: number;
  readonly trophyCount: number;
  readonly onBadgeClick: (tab: ArtistPreviewTab) => void;
  readonly tooltipId?: string | undefined;
}
export const ArtistActivityBadge: React.FC<ArtistActivityBadgeProps> = t0 => {
  const $ = _c(39);
  const {
    submissionCount,
    trophyCount,
    onBadgeClick,
    tooltipId: t1
  } = t0;
  const tooltipId = t1 === undefined ? "artist-activity-badge" : t1;
  const isMobile = useIsMobileDevice();
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const id = useId();
  const uniqueTooltipId = `${tooltipId}-${id}`;
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
  let config;
  let dataTooltipId;
  let initialTab;
  let showTooltip;
  let t2;
  let t3;
  if ($[0] !== hasTouchScreen || $[1] !== isMobile || $[2] !== submissionCount || $[3] !== trophyCount || $[4] !== uniqueTooltipId) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const state = getBadgeState(submissionCount, trophyCount);
      if (state === "none") {
        t3 = null;
        break bb0;
      }
      showTooltip = !isMobile && !hasTouchScreen;
      dataTooltipId = showTooltip ? uniqueTooltipId : undefined;
      config = BADGE_CONFIG[state];
      initialTab = config.initialTab;
      t2 = getTooltipContent(state, submissionCount, trophyCount);
    }
    $[0] = hasTouchScreen;
    $[1] = isMobile;
    $[2] = submissionCount;
    $[3] = trophyCount;
    $[4] = uniqueTooltipId;
    $[5] = config;
    $[6] = dataTooltipId;
    $[7] = initialTab;
    $[8] = showTooltip;
    $[9] = t2;
    $[10] = t3;
  } else {
    config = $[5];
    dataTooltipId = $[6];
    initialTab = $[7];
    showTooltip = $[8];
    t2 = $[9];
    t3 = $[10];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  const tooltipContent = t2;
  const ariaLabel = tooltipContent;
  const isPaletteIcon = config.icon === faPalette;
  const iconClassName = isPaletteIcon ? PALETTE_ICON_CLASS : TROPHY_ICON_CLASS;
  const describedById = showTooltip && isTooltipOpen ? uniqueTooltipId : undefined;
  let t4;
  if ($[11] !== initialTab || $[12] !== onBadgeClick) {
    t4 = e => {
      e.preventDefault();
      e.stopPropagation();
      setIsTooltipOpen(false);
      onBadgeClick(initialTab);
    };
    $[11] = initialTab;
    $[12] = onBadgeClick;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setIsTooltipOpen(true);
    t6 = () => setIsTooltipOpen(false);
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  let t7;
  if ($[16] !== dataTooltipId) {
    t7 = dataTooltipId && {
      "data-tooltip-id": dataTooltipId
    };
    $[16] = dataTooltipId;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== config.icon || $[19] !== iconClassName) {
    t8 = <FontAwesomeIcon icon={config.icon} className={iconClassName} />;
    $[18] = config.icon;
    $[19] = iconClassName;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== config.showDot) {
    t9 = config.showDot && <span className={BOTH_STATE_DOT_CLASS} aria-hidden="true" />;
    $[21] = config.showDot;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== ariaLabel || $[24] !== config.buttonClassName || $[25] !== describedById || $[26] !== t4 || $[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
    t10 = <button type="button" onClick={t4} onMouseEnter={t5} onMouseLeave={t6} className={config.buttonClassName} aria-label={ariaLabel} aria-describedby={describedById} {...t7}>{t8}{t9}</button>;
    $[23] = ariaLabel;
    $[24] = config.buttonClassName;
    $[25] = describedById;
    $[26] = t4;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== isTooltipOpen || $[32] !== showTooltip || $[33] !== tooltipContent || $[34] !== uniqueTooltipId) {
    t11 = showTooltip && <Tooltip id={uniqueTooltipId} role="tooltip" place="top" positionStrategy="absolute" content={tooltipContent} delayShow={300} opacity={1} style={TOOLTIP_STYLES} isOpen={isTooltipOpen} />;
    $[31] = isTooltipOpen;
    $[32] = showTooltip;
    $[33] = tooltipContent;
    $[34] = uniqueTooltipId;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t10 || $[37] !== t11) {
    t12 = <>{t10}{t11}</>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  return t12;
};